import {
  UseMutateAsyncFunction,
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AddDraftTableRowDto,
  DraftDataDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_DATA } from "../GetFormDraftData/useGetFormDraftData";

import { submissionsApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_ADD_DRAFT_TABLE_ROW = "ADD_DRAFT_TABLE_ROW";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

const addDraftTableRow = async (
  req: AddDraftTableRowDto,
): Promise<DraftDataDto[]> => {
  const response = await submissionsApi.submissionsControllerAddDraftTableRow(req);

  return response.data;
};

export const useAddDraftTableRow = (
  user: UserProfileDto | null,
  params: AddDraftTableRowDto,
): {
  addDraftTableRow: UseMutateFunction<DraftDataDto[], Error>;
  addDraftTableRowAsync: UseMutateAsyncFunction<DraftDataDto[], Error>;
  addDraftTableRowEnabled: boolean;
  addDraftTableRowError: Error | null;
  isSuccessAddDraftTableRow: boolean;
  isPendingAddDraftTableRow: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending, error } = useMutation({
    mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_ADD_DRAFT_TABLE_ROW, params],
    mutationFn: enabled
      ? (): Promise<DraftDataDto[]> => addDraftTableRow(params)
      : undefined,
    onSuccess(data) {
      const queryKey = [
        DOMAIN_FORM_SUBMISSIONS,
        ACTION_GET_FORM_DRAFT_DATA,
        params.formId,
      ];
      const currentData = queryClient.getQueryData<DraftDataDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData ? [...currentData, ...data] : [...data];
      });
    },
  });

  return {
    addDraftTableRow: mutate,
    addDraftTableRowAsync: mutateAsync,
    addDraftTableRowEnabled: enabled,
    addDraftTableRowError: error,
    isSuccessAddDraftTableRow: isSuccess,
    isPendingAddDraftTableRow: isPending,
  };
};
